/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * Normalizes canonical agent-run API payloads for Command Intelligence chat UI.
 * Drift control: cds-ai-service/backend/v4/agents/runs/service.js (toRunResponse),
 * derive-agent-response-text.js, mapRunToLegacyResponse tool/capability derivation.
 */

const normalizeTrimmedString = value => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

const getStructuredOutput = payload => {
  if (payload?.structuredOutput && typeof payload.structuredOutput === 'object') {
    return payload.structuredOutput;
  }

  if (payload?.metadata?.structuredOutput && typeof payload.metadata.structuredOutput === 'object') {
    return payload.metadata.structuredOutput;
  }

  return null;
};

/**
 * User-visible text from agent output (structured content wins over free-text response).
 * @param {Record<string, unknown>} output
 * @returns {string}
 */
export const deriveResponseText = output => {
  if (!output || typeof output !== 'object') {
    return '';
  }

  const structured = getStructuredOutput(output);
  const fromStructured = normalizeTrimmedString(structured?.content);

  if (fromStructured) {
    return fromStructured;
  }

  const fromResponse = normalizeTrimmedString(output.response);

  if (fromResponse) {
    return fromResponse;
  }

  const fromOutputString = normalizeTrimmedString(output.output);

  if (fromOutputString) {
    return fromOutputString;
  }

  return '';
};

/**
 * @param {Record<string, unknown>} output
 * @returns {{ toolCalls: object[], toolsUsed: string[], capabilitiesInvoked: string[] }}
 */
export const deriveToolsFromOutput = output => {
  const toolCalls = Array.isArray(output?.toolCalls) ? output.toolCalls : [];
  const toolsUsed = toolCalls.map(tool => tool.toolName || tool.name).filter(Boolean);
  const capabilitiesInvoked = toolCalls
    .filter(tool => (tool.toolName || tool.name) === 'invoke-capability')
    .map(tool => tool?.input?.id || tool?.args?.id)
    .filter(Boolean);

  return { toolCalls, toolsUsed, capabilitiesInvoked };
};

const FAILED_AGENT_RUN_STATUSES = new Set(['failed', 'error', 'cancelled']);

const buildRunErrorMetadata = runResponse => {
  const err = runResponse?.error;

  if (err && typeof err === 'object') {
    return {
      code: err.code ?? null,
      message: typeof err.message === 'string' ? err.message.slice(0, 2000) : null,
    };
  }

  return { code: null, message: null };
};

/**
 * @param {Record<string, unknown> | null | undefined} payload
 * @returns {boolean}
 */
export const isAgentRunFailed = payload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const status = typeof payload.status === 'string' ? payload.status.trim().toLowerCase() : '';

  return status.length > 0 && FAILED_AGENT_RUN_STATUSES.has(status);
};

/**
 * User-visible failure text from a canonical agent run payload.
 * @param {Record<string, unknown> | null | undefined} payload
 * @returns {string}
 */
export const getAgentRunFailureMessage = payload => {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const fromError = normalizeTrimmedString(payload.error?.message);

  if (fromError) {
    const code = normalizeTrimmedString(payload.error?.code);

    if (code) {
      return `[${code}] ${fromError}`;
    }

    return fromError;
  }

  const decisionRecord =
    payload.decisionRecord && typeof payload.decisionRecord === 'object' ? payload.decisionRecord : null;
  const fromDecisionError = normalizeTrimmedString(decisionRecord?.errorMessage);

  if (fromDecisionError) {
    const decisionCode = normalizeTrimmedString(decisionRecord?.errorCode);

    if (decisionCode) {
      return `[${decisionCode}] ${fromDecisionError}`;
    }

    return fromDecisionError;
  }

  const fromSummary = normalizeTrimmedString(decisionRecord?.decisionSummary);

  if (fromSummary) {
    return fromSummary;
  }

  return '';
};

/**
 * On localhost, promote failed run errors into chat response text.
 * @param {{ runPayload: Record<string, unknown>, responseText: string, metadata: Record<string, unknown> }} input
 * @param {{ verboseErrors?: boolean }} options
 * @returns {{ responseText: string, metadata: Record<string, unknown>, isRunError: boolean }}
 */
export const applyVerboseRunFailure = ({ runPayload, responseText, metadata }, { verboseErrors = false } = {}) => {
  if (!verboseErrors || !isAgentRunFailed(runPayload)) {
    return { responseText, metadata, isRunError: false };
  }

  const failureText = getAgentRunFailureMessage(runPayload);

  if (!failureText) {
    return { responseText, metadata, isRunError: false };
  }

  return {
    responseText: failureText,
    metadata,
    isRunError: true,
  };
};

export const isCanonicalRunShape = payload =>
  payload !== null &&
  payload !== undefined &&
  typeof payload === 'object' &&
  ((payload.output !== null && payload.output !== undefined && typeof payload.output === 'object') ||
    (typeof payload.runRef === 'string' && payload.runRef.trim() !== '') ||
    (typeof payload.status === 'string' && payload.status.trim() !== ''));

/**
 * Picks the canonical agent-run body from a create-channel / stream / LRR envelope.
 * Create-channel returns LRR fields on the parent with the run under `initialResponse` or `result`.
 * @param {Record<string, unknown> | null | undefined} envelope
 * @returns {Record<string, unknown>}
 */
export const pickAgentRunPayload = envelope => {
  if (envelope === null || envelope === undefined || typeof envelope !== 'object') {
    return {};
  }

  if (isCanonicalRunShape(envelope)) {
    return envelope;
  }

  const { initialResponse } = envelope;

  if (isCanonicalRunShape(initialResponse)) {
    return initialResponse;
  }

  const { result } = envelope;

  if (isCanonicalRunShape(result)) {
    return result;
  }

  return envelope;
};

/**
 * @param {Record<string, unknown> | null | undefined} payload - canonical agent run (toRunResponse shape)
 * @returns {{ responseText: string, metadata: Record<string, unknown> }}
 */
export const readAgentRunResult = payload => {
  if (!payload || typeof payload !== 'object') {
    return { responseText: '', metadata: {} };
  }

  const output = payload.output && typeof payload.output === 'object' ? payload.output : {};
  const status = typeof payload.status === 'string' ? payload.status.trim() : '';
  const { toolCalls, toolsUsed, capabilitiesInvoked } = deriveToolsFromOutput(output);
  const responseText = deriveResponseText(output);
  const metadata = {};

  if (payload.usage && typeof payload.usage === 'object') {
    metadata.usage = payload.usage;
  }

  if (output.model !== null && output.model !== undefined && String(output.model).trim() !== '') {
    metadata.model = output.model;
  }

  if (toolCalls.length > 0) {
    metadata.toolCalls = toolCalls;
  }

  if (toolsUsed.length > 0) {
    metadata.toolsUsed = toolsUsed;
  }

  if (capabilitiesInvoked.length > 0) {
    metadata.capabilitiesInvoked = capabilitiesInvoked;
  }

  if (typeof payload.runRef === 'string' && payload.runRef.trim() !== '') {
    metadata.runRef = payload.runRef.trim();
  }

  const topLevelMessageRef = normalizeTrimmedString(payload.messageRef);

  if (topLevelMessageRef) {
    metadata.messageRef = topLevelMessageRef;
    metadata.crn = topLevelMessageRef;
  }

  if (typeof payload.agentRef === 'string' && payload.agentRef.trim() !== '') {
    metadata.agentRef = payload.agentRef.trim();
  }

  if (status.length > 0) {
    metadata.runStatus = status;
  }

  if (status === 'awaiting_approval' && payload.pendingApproval && typeof payload.pendingApproval === 'object') {
    metadata.pendingApproval = payload.pendingApproval;
  }

  if (status.length > 0 && status !== 'succeeded' && status !== 'awaiting_approval') {
    const runError = buildRunErrorMetadata(payload);

    if (runError.message || runError.code) {
      metadata.runError = runError;
    }
  }

  if (payload.decisionRecord !== undefined && payload.decisionRecord !== null) {
    metadata.decisionRecord = payload.decisionRecord;
  }

  const structuredOutput = getStructuredOutput(output);

  if (structuredOutput) {
    metadata.structuredOutput = structuredOutput;
  }

  if (output.conversationRef) {
    metadata.conversationRef = output.conversationRef;
  } else if (payload.conversationRef) {
    metadata.conversationRef = payload.conversationRef;
  }

  if (Array.isArray(payload.creditLimitWarnings) && payload.creditLimitWarnings.length > 0) {
    metadata.creditLimitWarnings = payload.creditLimitWarnings;
  }

  if (payload.metadata && typeof payload.metadata === 'object') {
    const metadataMessageRef = normalizeTrimmedString(payload.metadata.messageRef);

    if (metadataMessageRef) {
      metadata.messageRef = metadataMessageRef;
      metadata.crn = metadataMessageRef;
    } else if (payload.metadata.crn) {
      metadata.crn = payload.metadata.crn;
    }

    if (payload.metadata.requestId) {
      metadata.requestId = payload.metadata.requestId;
    }
  }

  return { responseText, metadata };
};

/**
 * User-facing message when an LRR finishes in a failed terminal state.
 * @param {{ status?: string, error?: string, result?: { error?: { message?: string } } } | null | undefined} lrr
 * @returns {string}
 */
export const getLrrFailureMessage = lrr => {
  const result = lrr?.result && typeof lrr.result === 'object' ? lrr.result : null;
  const fromRun = getAgentRunFailureMessage(result);

  if (fromRun) {
    return fromRun;
  }

  const directError = typeof lrr?.error === 'string' ? lrr.error : '';

  if (directError.trim() !== '') {
    return directError.trim();
  }

  return 'Long running request failed.';
};
